import {
  PenLine,
  MessageSquare,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileText,
} from 'lucide-react';
import type { Station } from '@hcc/types';
import type { PersonApi } from '../../hooks/usePeople';
import { FlagsButton } from '../../components/person/FlagsButton';
import { PersonAvatar } from '../../components/person/PersonAvatar';
import { Button } from '../../components/ui/Button';
import { Pill } from '../../components/ui/Pill';

interface IssuedSectionProps {
  station: Station;
  people: PersonApi[];
  searching: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  onReissue: (person: PersonApi) => void;
  onNotes: (person: PersonApi) => void;
}

/**
 * Already-issued section — collapsed by default, auto-expands during search
 * so matches there aren't hidden. "Re-issue" lets the operator open the
 * drawer again for a swap / additional item; the chain just appends.
 */
export function IssuedSection({
  station,
  people,
  searching,
  expanded,
  onToggleExpanded,
  onReissue,
  onNotes,
}: IssuedSectionProps) {
  return (
    <section
      aria-label="Issued"
      className="mt-4 overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm"
    >
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        className={`flex w-full items-center justify-between px-5 py-3 text-left transition-colors ${
          expanded ? 'border-b border-divider/10 bg-success-soft/10' : 'hover:bg-surface-2'
        }`}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-success-soft-fg" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-success-soft-fg" />
          )}
          <CheckCircle2 className="h-3.5 w-3.5 text-success-soft-fg" />
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
            Issued
          </span>
          <Pill tone="success" size="sm">
            {people.length}
          </Pill>
        </div>
        <span className="text-[11px] text-fg-muted">
          {expanded ? (searching ? 'showing matches' : 'tap to hide') : 'tap to show'}
        </span>
      </button>
      {expanded ? (
        people.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-fg-muted">
            {searching
              ? 'No matches in the issued list.'
              : 'No one has been issued kit at this station yet.'}
          </div>
        ) : (
          <ul className="divide-y divide-divider/10">
            {people.map((p) => (
              <li
                key={p.id}
                className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <PersonAvatar
                    fullName={p.full_name}
                    photoKey={p.current_photo_s3_key}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-medium text-fg">{p.full_name}</span>
                      <Pill tone="success" icon={<CheckCircle2 className="h-3 w-3" />}>
                        Issued
                      </Pill>
                      {wasPreDay(p.stations?.[station]?.at, p.arrived_at) ? (
                        <Pill tone="warning" size="sm">
                          Pre-day
                        </Pill>
                      ) : null}
                      <FlagsButton
                        personId={p.id}
                        personName={p.full_name}
                        count={p.active_flags ?? 0}
                      />
                    </div>
                    <div className="mt-0.5 text-xs text-fg-muted">
                      {p.position ?? 'Position unknown'}
                      {p.email ? ` · ${p.email}` : ''}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {p.stations?.[station]?.latest_kit_issued_event_id ? (
                    <a
                      href={`/api/admin/kit-receipts/${p.stations[station].latest_kit_issued_event_id}/pdf?person_id=${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex h-8 items-center gap-1.5 rounded-token-sm border border-transparent px-2 text-xs font-medium text-accent transition-colors hover:bg-accent/10"
                      title="Open the signed receipt PDF for this issuance"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      View receipt
                    </a>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<MessageSquare className="h-3.5 w-3.5" />}
                    onClick={() => onNotes(p)}
                  >
                    Notes
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<PenLine className="h-3.5 w-3.5" />}
                    onClick={() => onReissue(p)}
                    title="Open the issue drawer again to record an extra or a swap"
                  >
                    Re-issue
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </section>
  );
}

/**
 * True when this station's most recent issuance predates the person's
 * arrival — i.e. the kit was handed out before mobilisation day.
 * If either timestamp is missing, fall back to false (no badge).
 */
function wasPreDay(
  issuedAt: string | null | undefined,
  arrivedAt: string | null | undefined,
): boolean {
  if (!issuedAt || !arrivedAt) return false;
  return new Date(issuedAt).getTime() < new Date(arrivedAt).getTime();
}
